import { Box } from "@mui/material";
import { IRankingData } from "src/components/common/ranking/types";
import { Typography } from "src/components-mui/base/typography/typography";
import { HoverCard, IHoverCardProps } from "src/components-mui/common/hover-card/hover-card";
import { Ranking } from "src/components-mui/common/ranking/ranking";
import { TraderAvatar } from "src/components-mui/common/trader-avatar/trader-avatar";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { ITrader, IUser, IWikifolio } from "src/types/common";

type IUserOrTrader = Pick<IUser, "id" | "firstName" | "lastName" | "nickName" | "isDeleted"> | ITrader;

export interface IWikifolioInfoCardProps extends Pick<IWikifolio, "symbol" | "shortDescription">, Omit<IHoverCardProps, "href" | "children"> {
    trader: IUserOrTrader;
    ranking: IRankingData;
    hidePoints?: boolean;
}

/**
 * WikifolioInfoCard component is responsible for displaying information about a single Wikifolio.
 * It takes various props to render details such as the Wikifolio's name, symbol, performance, and other relevant data.
 * The component is designed to be used within a list or grid of Wikifolio cards, providing a consistent and informative
 * presentation of each Wikifolio's key information.
 */
export const WikifolioInfoCard = ({ trader, ranking, symbol, hidePoints, shortDescription, sx, ...hoverCardProps }: IWikifolioInfoCardProps) => {
    const { buildWikifolioLink } = useLinkBuilder();
    const getUserName = useGetUserName();

    return (
        <HoverCard
            component="a"
            href={buildWikifolioLink(symbol)}
            {...hoverCardProps}
            sx={[{ boxShadow: 1, color: "inherit", textDecoration: "none", "&:hover": { boxShadow: 1 } }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            <TraderAvatar size="sm" trader={trader} sx={{ flexShrink: 0, mr: 1 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1, fontSize: "sm" }}>
                <Typography noWrap sx={{ fontSize: "inherit", fontWeight: "bold", wordBreak: "break-all" }}>
                    {shortDescription}
                </Typography>
                <Typography noWrap sx={{ fontSize: "inherit", wordBreak: "break-all" }}>
                    {getUserName(trader)}
                </Typography>
            </Box>
            <Ranking
                sx={{ flexShrink: 0, alignItems: "flex-end" }}
                size="sm"
                data={ranking}
                labelProps={{ sx: { fontSize: "sm" } }}
                hidePoints={hidePoints}
            />
        </HoverCard>
    );
};
